import { Dispatch, memo, SetStateAction, useCallback, useState } from "react";

import { Box, Stack, TextInput } from "@mantine/core";
import { EditorState, LexicalEditor } from "lexical";
import throttle from "lodash/throttle";

import { EventType } from "apollo/graphql.generated";
import GateSelect, { SelectableGate } from "components/GateSelect";
import useAnalytics from "hooks/useAnalytics";

import Editor from "../Editor";

import { getContent, setContent } from "./state";

interface ContentTabProps {
  content: string;
  gates: SelectableGate[];
  gateId?: string | null;
  pageName?: string;
  selectedGateIdOnChange(id: string): void;
  pageNameOnChange(id: string): void;
  onEditorChange?(editorState: EditorState, editor: LexicalEditor): void;
  onFileSelected?(f: File): void;
}

const ContentTab = ({
  content,
  gates,
  gateId,
  pageName,
  selectedGateIdOnChange,
  pageNameOnChange,
  onEditorChange,
  onFileSelected,
}: ContentTabProps) => {
  const { trackEvent } = useAnalytics();
  const [containsText, setText] = useState<boolean>(false);
  const [containsEmbed, setEmbed] = useState<boolean>(false);
  const [containsAudio, setAudio] = useState<boolean>(false);
  const [containsImage, setImage] = useState<boolean>(false);
  const [containsVideo, setVideo] = useState<boolean>(false);
  const [containsLink, setLink] = useState<boolean>(false);

  const emitAnalytics = (contentType: string) => {
    const data: Record<string, boolean> = {};
    data[contentType] = true;
    trackEvent(EventType.CreateGateEditor_EditContent, data);
  };

  const setters: Record<
    string,
    [boolean, Dispatch<SetStateAction<boolean>>, string]
  > = {
    text: [containsText, setText, "addedText"],
    link: [containsLink, setLink, "addedLink"],
    image: [containsImage, setImage, "addedImage"],
    audio: [containsAudio, setAudio, "addedAudio"],
    video: [containsVideo, setVideo, "addedVideo"],
    embed: [containsEmbed, setEmbed, "addedEmbed"],
  };

  const onEditorChangeThrottle = useCallback(
    throttle(
      (editorState, editor) => {
        const newContent = JSON.stringify(editorState);
        if (newContent === getContent()?.editorContent?.state) {
          return;
        }
        setContent(newContent);
        onEditorChange?.(editorState, editor);

        const emitList = new Set<string>();
        // Go through all expected content types
        // if editor didn't previously contain this type, mark to emit an event
        for (const [, val] of editorState._nodeMap) {
          if (Object.keys(setters).includes(val.__type)) {
            if (!setters[val.__type][0]) {
              emitList.add(val.__type);
            }
          }
        }
        // mark as seen and emit new content-type-added event
        for (const contentType of emitList) {
          setters[contentType][1](true);
          emitAnalytics(setters[contentType][2]);
        }
      },
      1000,
      { leading: true }
    ),
    [
      onEditorChange,
      containsText,
      containsLink,
      containsImage,
      containsAudio,
      containsVideo,
      containsEmbed,
    ]
  );
  return (
    <Stack spacing={32}>
      <TextInput
        placeholder="Only you can see this name"
        label="Page name"
        value={pageName}
        onChange={(event) => pageNameOnChange(event.currentTarget.value)}
      />
      <GateSelect
        gates={gates}
        value={gateId ?? ""}
        onChange={selectedGateIdOnChange}
      />
      <Box
        sx={{
          minHeight: "350px",
        }}
      >
        <Editor
          initialEditorState={content || undefined}
          editable
          showEmbedNodes={false}
          onEditorChange={onEditorChangeThrottle}
          onFileSelected={onFileSelected}
        />
      </Box>
    </Stack>
  );
};

export default memo(ContentTab);
